import React from 'react';
import axios from "axios";
import { Navigate } from 'react-router-dom';

// Public pages
import Signup from '../pages/auth/Signup';
import Login from '../pages/auth/Login';
import Home from '../pages/dashboard/Home';
import Faqs from '../pages/dashboard/Faqs';
import Profile from '../pages/dashboard/Profile';
import ResetPassword from '../pages/auth/ResetPassword';
import SetPassword from '../pages/auth/SetPassword';
import ForgotPassword from '../pages/auth/ForgotPassword';
import UsersTable from '../pages/dashboard/users/UsersTable';
import ListQuestions from '../pages/dashboard/questions/ListQuestions';
import AnswersToQuestions from '../pages/dashboard/questions/AnswersToQuestions';
import ApiQuestionsPage from '../pages/dashboard/questions/ApiQuestionsPage';
import EmissionsQuestionsPage from '../pages/dashboard/questions/EmissionsQuestionsPage';
import ListQuestionsForManagement from '../pages/dashboard/questionsManagement/ListQuestionsForManagement';
import Reporting from '../pages/dashboard/reporting/Reporting';
import DetailedReport from '../pages/dashboard/reporting/DetailedReport';
import Section91Report from '../pages/dashboard/reporting/Section91Report';
import VendorsPage from '../pages/dashboard/vendors/VendorsPage';
import VendorDetailPage from '../pages/dashboard/vendors/VendorDetailPage';
import VendorLoginPage from '../pages/vendor/VendorLoginPage';
import VendorPortalPage from '../pages/vendor/VendorPortalPage';
import StepsWrapper from '../pages/dashboard/questionair/StepsWrapper';
// Unified API Component
import UnifiedApiComponent from '../pages/dashboard/questions/api/UnifiedApiComponent';
import ApiTestPage from '../pages/dashboard/questions/api/ApiTestPage';

// Mock authentication and first login functions
const isAuthenticated = () => {
  return localStorage.getItem('accessToken') !== null;
};

const isFirstLogin = () => {
  return localStorage.getItem('isFirstLogin') === 'true';
};

// ProtectedRoute for private pages
function ProtectedRoute({ children, allowedRoles = [] }) {
  if (!isAuthenticated()) {
    return <Navigate to="/login" />;
  }

  if (isFirstLogin() && window.location.pathname !== '/reset-password') {
    return <Navigate to="/reset-password" />;
  }

  const userRole = localStorage.getItem('role'); // Assuming user role is stored in localStorage

  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    return <Navigate to="/home" />; // Redirect unauthorized users to home
  }

  return children;
}


// PublicRoute for public pages
function PublicRoute({ children }) {
  if (isAuthenticated()) {
    return <Navigate to="/Home" />; // Redirect logged-in users to the home page
  }

  return children;
}

export const fetchSubTopics = async () => {
  try {
    const response = await axios.get("/disclosure-topics");

    if (response.data.status && response.data.topics) {
      return response.data.topics.map((topic) => ({
        path: `/questions-answers?topic=${encodeURIComponent(topic)}`,
        label: topic,
      }));
    }
  } catch (error) {
    console.error("Error fetching subtopics:", error);
  }
  return [];
};

const routes = [
  {
    path: '/',
    component: (
      <PublicRoute>
        <Signup />
      </PublicRoute>
    ),
    isProtected: false,
    label: '',
    icon: null, // Person-plus icon for signup
  },
  {
    path: '/login',
    component: (
      <PublicRoute>
        <Login />
      </PublicRoute>
    ),
    isProtected: false,
    label: '',
    icon: null, // Box-arrow-in-right icon for login
  },
  {
    path: '/vendor/login',
    component: <VendorLoginPage />,
    isProtected: false,
    label: '',
    icon: null,
  },
  {
    path: '/vendor/portal',
    component: <VendorPortalPage />,
    isProtected: false,
    label: '',
    icon: null,
  },
  {
    path: '/forgot-password',
    component: (
      <PublicRoute>
        <ForgotPassword />
      </PublicRoute>
    ),
    isProtected: false,
    label: '',
    icon: null,
  },
  {
    path: '/reset-password',
    component: (
      <ProtectedRoute>
        <ResetPassword />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    icon: null,
    restrictedTo: ['superAdmin'],
  },
  {
    path: '/set-password',
    component: <SetPassword />,
    isProtected: false,
    label: '',
    icon: null,
  },
  {
    path: '/profile',
    component: (
      <ProtectedRoute>
        <Profile />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    icon: null, 
  },
  {
    path: '/Home',
    component: (
      <ProtectedRoute>
        <Home />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: 'Home',
    icon: <i className="bi bi-house-door"></i>,
    role: ['companyAdmin', 'user'],
    restrictedTo: [''],

  },
  {
    path: '/register-company',
    component: (
      <ProtectedRoute allowedRoles={['companyAdmin']}>
        <StepsWrapper />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: 'Register Company',
    restrictedTo: ['user','superAdmin'],
    icon: <i className="bi bi-building"></i>,
  },
  {
    path: '/users',
    component: (
      <ProtectedRoute allowedRoles={['companyAdmin','superAdmin']}>
        <UsersTable />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: 'Users',
    restrictedTo: ['user'],
    icon: <i className="bi bi-people"></i>,
  },
  {
    path: '/questions-management',
    component: (
      <ProtectedRoute allowedRoles={['superAdmin']}>
        < ListQuestionsForManagement/>
      </ProtectedRoute>
    ),
    isProtected: true,
    label: 'Questions Management',
    restrictedTo: ['companyAdmin','user'],
    icon: <i className="bi bi-people"></i>,
  },
  {
    path: '/questions',
    component: (
      <ProtectedRoute allowedRoles={['companyAdmin']}>
        <ListQuestions />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: 'Questions Assignment',
    restrictedTo: ['user','superAdmin'],
    icon: <i className="bi bi-chat-dots"></i>,
  },
  {
    path: "/questions-answers",
    component: (
      <ProtectedRoute allowedRoles={["user"]}>
        <AnswersToQuestions />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: "Questions/Answers",
    restrictedTo: ["companyAdmin", "superAdmin"],
    icon: <i className="bi bi-file-earmark-text"></i>,
    subTopics: fetchSubTopics, // Fetch subtopics dynamically
  },
  {
    path: "/api-questions",
    component: (
      <ProtectedRoute allowedRoles={["user"]}>
        <ApiQuestionsPage />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ["companyAdmin", "superAdmin"],
    icon: null,
  },
  {
    path: "/emissions-questions",
    component: (
      <ProtectedRoute allowedRoles={["user", "companyAdmin", "superAdmin"]}>
        <EmissionsQuestionsPage />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: 'Emissions Questions',
    restrictedTo: ["companyAdmin", "superAdmin"],
    icon: <i className="bi bi-cloud-rain"></i>,
  },
  {
    path: '/reporting',
    component: (
      <ProtectedRoute allowedRoles={['companyAdmin', 'superAdmin']}>
        <Reporting />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: 'Reporting',
    restrictedTo: ['user'],
    icon: <i className="bi bi-graph-up"></i>,
  },
  {
    path: '/vendors/:id',
    component: (
      <ProtectedRoute allowedRoles={['companyAdmin', 'superAdmin']}>
        <VendorDetailPage />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ['user'],
    icon: null,
  },
  {
    path: '/vendors',
    component: (
      <ProtectedRoute allowedRoles={['companyAdmin', 'superAdmin']}>
        <VendorsPage />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: 'Vendors',
    restrictedTo: ['user'],
    icon: <i className="bi bi-box-seam"></i>,
  },
  {
    path: '/detailed-report',
    component: (
      <ProtectedRoute allowedRoles={['companyAdmin', 'superAdmin']}>
        <DetailedReport />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ['user'],
    icon: null,
  },
  {
    path: '/section-91-report',
    component: (
      <ProtectedRoute allowedRoles={['companyAdmin', 'superAdmin']}>
        <Section91Report />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ['user'],
    icon: null,
  },
  {
    path: '/faqs',
    component: (
      <ProtectedRoute>
        <Faqs />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: 'FAQs',
    restrictedTo: [''],

    icon: <i className="bi bi-question-circle"></i>,
  },
  // API Question Routes - Using Unified Component
  // Individual routes for backward compatibility (pass single API number)
  {
    path: '/api/1',
    component: (
      <ProtectedRoute allowedRoles={['user']}>
        <UnifiedApiComponent apiNumbers={[1]} />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ['companyAdmin', 'superAdmin'],
    icon: null,
  },
  {
    path: '/api/2',
    component: (
      <ProtectedRoute allowedRoles={['user']}>
        <UnifiedApiComponent apiNumbers={[2]} />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ['companyAdmin', 'superAdmin'],
    icon: null,
  },
  {
    path: '/api/3',
    component: (
      <ProtectedRoute allowedRoles={['user']}>
        <UnifiedApiComponent apiNumbers={[3]} />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ['companyAdmin', 'superAdmin'],
    icon: null,
  },
  {
    path: '/api/4',
    component: (
      <ProtectedRoute allowedRoles={['user']}>
        <UnifiedApiComponent apiNumbers={[4]} />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ['companyAdmin', 'superAdmin'],
    icon: null,
  },
  {
    path: '/api/5',
    component: (
      <ProtectedRoute allowedRoles={['user']}>
        <UnifiedApiComponent apiNumbers={[5]} />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ['companyAdmin', 'superAdmin'],
    icon: null,
  },
  {
    path: '/api/6',
    component: (
      <ProtectedRoute allowedRoles={['user']}>
        <UnifiedApiComponent apiNumbers={[6]} />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ['companyAdmin', 'superAdmin'],
    icon: null,
  },
  {
    path: '/api/7',
    component: (
      <ProtectedRoute allowedRoles={['user']}>
        <UnifiedApiComponent apiNumbers={[7]} />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ['companyAdmin', 'superAdmin'],
    icon: null,
  },
  {
    path: '/api/8',
    component: (
      <ProtectedRoute allowedRoles={['user']}>
        <UnifiedApiComponent apiNumbers={[8]} />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ['companyAdmin', 'superAdmin'],
    icon: null,
  },
  {
    path: '/api/9',
    component: (
      <ProtectedRoute allowedRoles={['user']}>
        <UnifiedApiComponent apiNumbers={[9]} />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ['companyAdmin', 'superAdmin'],
    icon: null,
  },
  {
    path: '/api/10',
    component: (
      <ProtectedRoute allowedRoles={['user']}>
        <UnifiedApiComponent apiNumbers={[10]} />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ['companyAdmin', 'superAdmin'],
    icon: null,
  },
  {
    path: '/api/11',
    component: (
      <ProtectedRoute allowedRoles={['user']}>
        <UnifiedApiComponent apiNumbers={[11]} />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ['companyAdmin', 'superAdmin'],
    icon: null,
  },
  {
    path: '/api/12',
    component: (
      <ProtectedRoute allowedRoles={['user']}>
        <UnifiedApiComponent apiNumbers={[12]} />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ['companyAdmin', 'superAdmin'],
    icon: null,
  },
  // Unified route for multiple APIs (e.g., /api/unified?apis=2,5,7)
  {
    path: '/api/unified',
    component: (
      <ProtectedRoute allowedRoles={['user']}>
        <UnifiedApiComponent />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: ['companyAdmin', 'superAdmin'],
    icon: null,
  },
  // API Test Page - Dashboard for testing all APIs
  {
    path: '/api/test',
    component: (
      <ProtectedRoute allowedRoles={['user', 'companyAdmin', 'superAdmin']}>
        <ApiTestPage />
      </ProtectedRoute>
    ),
    isProtected: true,
    label: '',
    restrictedTo: [],
    icon: null,
  },
];


export default routes;